import PropTypes from 'prop-types';
import { extend, useDataModel } from '@thd-nucleus/data-sources';
import { useHelmet } from '@thd-nucleus/thd-helmet';
import { EMT_PAGE_NAMES, LAST_PATH, OPEN_GRAPH_FACEBOOK_ADMIN } from '../../constants';
import { filterEmtMetadataOverrides } from '../../helpers/emt-content-helpers';
import { generateStorePageCanonical, generateStorePageTitle } from '../../helpers/pps-helpers';
import { generateTags } from '../../utils/generate-tags';
import { emtContentModel, storeSearchModel } from '../../models';
import { generateHomeServicesDesc, getHomeServicesSchemas } from './home-services-helpers';

// --- Tag Handler ---

const homeServicesHandler = ({ data: { emtData, storeData }, props: { storeId } }) => {
  // Extract store details data once
  const storeInfo = {
    ...storeData?.storeSearch?.stores?.[0],
    storeId
  };
  const {
    name: storeName,
    address: { city, state, postalCode } = {},
    storeDetailsPageLink,
    storeServicesPhone
  } = storeInfo;

  const canonicalURL = generateStorePageCanonical({
    storeDetailsPageLink, urlSuffix: LAST_PATH.HOME_SERVICES
  });

  // Extract emtContent data once
  const emtContent = emtData?.emtContent?.content || {};
  const emtContentMetadata = emtContent?.homeServicesMetadata || {};
  const emtContentFileData = emtContent?.[EMT_PAGE_NAMES.HOME_SERVICES] || {};

  const fallbackTitle = generateStorePageTitle({
    department: 'Home Services', storeName, city, state, postalCode
  });
  const fallbackDescription = generateHomeServicesDesc({ storeName, storeServicesPhone });
  const {
    metaDescription = fallbackDescription,
    ogDescription = fallbackDescription,
    ogImage,
    ogTitle = fallbackTitle,
    ogType = 'PPS Services',
    schema: emtSchema,
    title = fallbackTitle
  } = filterEmtMetadataOverrides(emtContentMetadata);

  const schemas = getHomeServicesSchemas({ emtContentFileData, emtSchema, storeInfo });

  const preloadImages = [{
    href: 'https://assets.homedepot-static.com/mcstore/images/HomeServices-938x157Desktop.png'
  }];

  const tags = generateTags({
    canonical: canonicalURL,
    description: metaDescription,
    fbAdmins: OPEN_GRAPH_FACEBOOK_ADMIN,
    ogDescription,
    ogImage,
    ogSiteName: 'The Home Depot',
    ogTitle,
    ogType,
    ogUrl: canonicalURL,
    preloadImages,
    schemas,
    title
  });

  return tags;
};

// --- Main Component ---

const HomeServicesMetadata = ({ storeId }) => {
  const emtContentOptions = {
    variables: {
      contentId: storeId,
      pageName: EMT_PAGE_NAMES.HOME_SERVICES
    }
  };
  const storeSearchOptions = {
    variables: {
      storeSearchInput: storeId,
      pagesize: '1'
    }
  };

  const { data: emtData } = useDataModel('emtContent', emtContentOptions);
  const { data: storeData } = useDataModel('storeSearch', storeSearchOptions);

  const data = { emtData, storeData };
  const props = { storeId };

  useHelmet('homeServicesMetadata', { data, props, skipCleanup: true }, homeServicesHandler, [storeId]);

  return null;
};

// --- Component Static Properties (dataModel, propTypes, etc.) ---

HomeServicesMetadata.displayName = 'HomeServicesMetadata';

HomeServicesMetadata.dataModel = extend(
  emtContentModel,
  storeSearchModel
);

HomeServicesMetadata.propTypes = {
  storeId: PropTypes.string.isRequired
};

export { HomeServicesMetadata };